import React, { useState, useEffect } from 'react'
import { X, MessageCircle } from 'lucide-react'
import { ChatInterface } from './ChatInterface'
import { Logo } from '../ui/Logo'
import { useIsMobile } from '../../hooks/useIsMobile'
import { useChat } from '../../contexts/ChatContext'

export const PublicChatLauncher: React.FC = () => {
  const { isChatOpen, openChat, closeChat } = useChat()
  const [open, setOpen] = useState(false)
  const isMobile = useIsMobile()

  useEffect(() => {
    setOpen(isChatOpen)
  }, [isChatOpen])

  useEffect(() => {
    if (!open) {
      closeChat()
    }
  }, [open, closeChat])

  useEffect(() => {
    const handleOpenChat = () => {
      setOpen(true)
      openChat()
    }
    
    window.addEventListener('openChat', handleOpenChat)
    return () => window.removeEventListener('openChat', handleOpenChat)
  }, [openChat])

  const handleStartProcess = () => {
    // Account creation happens through chat, no need to navigate
  }

  // Mobile: Full screen mode
  if (open && isMobile) {
    return (
      <div className="fixed inset-0 z-[9999] bg-white dark:bg-slate-900">
        <div className="h-full flex flex-col">
          <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-2 py-1 bg-white dark:bg-slate-800">
            <Logo className="h-5 w-auto opacity-80" />
            <button
              onClick={() => {
                setOpen(false)
                closeChat()
              }}
              className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
            >
              <X className="h-4 w-4 text-slate-500 dark:text-slate-400" />
            </button>
          </div>
          <div className="flex-1 overflow-hidden">
            <ChatInterface 
              onStartProcess={handleStartProcess}
              className="h-full"
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Floating launcher button - Bottom Right */}
      {!open && (
        <div className="fixed bottom-6 right-6 z-[9999] bg-transparent" style={{ backgroundColor: "transparent", background: "transparent", boxShadow: "none" }}>
          {/* Pulsing Ring Animation */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#69adba] to-[#5a9aa6] animate-ping opacity-20"></div>
          
          {/* Main Button */}
          <button
            onClick={() => {
              setOpen(true)
            }}
            className="relative group h-16 w-16 lg:h-18 lg:w-18 bg-gradient-to-br from-[#69adba] via-[#5fbdce] to-[#69adba] hover:from-[#5fbdce] hover:via-[#69adba] hover:to-[#5fbdce] text-white rounded-full shadow-[0_20px_60px_rgba(105,173,186,0.5),0_8px_24px_rgba(0,0,0,0.2),0_0_0_1px_rgba(255,255,255,0.1)_inset] hover:shadow-[0_24px_72px_rgba(105,173,186,0.6),0_12px_32px_rgba(0,0,0,0.25),0_0_0_1px_rgba(255,255,255,0.2)_inset] flex items-center justify-center hover:scale-110 active:scale-95 transition-all duration-500 ease-out"
          >
            {/* Glossy Shine Effect */}
            <div className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-0 transition-opacity"></div>
            
            {/* Icon with subtle animation */}
            <MessageCircle className="h-8 w-8 lg:h-9 lg:w-9 relative z-10 drop-shadow-lg group-hover:rotate-12 transition-transform duration-300" fill="none" style={{ backgroundColor: "transparent", background: "transparent", boxShadow: "none" }} />
          </button>
        </div>
      )}

      {/* Desktop: Docked Chat Panel (bottom-right) */}
      {open && !isMobile && (
        <div className="fixed bottom-6 right-6 z-[9999] w-[420px] max-w-[90vw] h-[600px] min-h-[420px] max-h-[85vh] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl overflow-hidden resize-y">
          <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-2 py-1 bg-white dark:bg-slate-800">
            <Logo className="h-5 w-auto opacity-80" />
            <button
              onClick={() => {
                setOpen(false)
                closeChat()
              }}
              className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
            >
              <X className="h-4 w-4 text-slate-500 dark:text-slate-400" />
            </button>
          </div>
          <div className="h-[calc(100%-32px)]">
            <ChatInterface 
              onStartProcess={handleStartProcess}
              className="h-full"
            />
          </div>
        </div>
      )}
    </>
  )
}

